import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowUpDown, GripVertical, Loader2, X } from "lucide-react";
import type { DashboardField, FieldJob, Folder } from "../types/contract";
import {
  useFields,
  useFolders,
  useMoveField,
  useOrgFieldJobs,
  useRenameField,
  useReparentFolder,
  useRxCoverage,
} from "../hooks/queries";
import {
  useUiStore,
  DND_FIELD,
  DND_FOLDER,
  ROOT_DROP,
} from "../stores/uiStore";
import { useDragAutoScroll } from "../hooks/useDragAutoScroll";
import { computeActionFlags } from "../lib/actionFlags";
import { flagCoverage } from "../lib/fertSets";
import { dismissFlag, dismissKey, loadDismissals } from "../lib/flagDismissals";
import { useFlagDismissBus } from "../stores/flagDismissBus";
import { fieldJobsSummary, jobsSummaryTitle } from "../lib/fieldJobs";
import { useRecSetsBus } from "../stores/recSetsBus";
import { useOrgStore } from "../stores/orgStore";
import { FolderRow } from "../features/folder-row/FolderRow";
import { InlineEdit } from "./InlineEdit";
import { FieldActionPanels, FieldActionsRow } from "./FieldActionsBar";
import { MergeWatchStrip } from "../features/merge-watch/MergeWatchStrip";
import { useFieldToolsStore } from "../stores/fieldToolsStore";
import {
  activeFlagLabels,
  collapseIgnoringSearch,
  folderSearch,
  searchKeepsField,
  searchOpenFolderIds,
} from "../lib/fieldSearch";

const col = createColumnHelper<DashboardField>();

// A freshly-created field (drawn/JD) can momentarily come back with a null/absent
// acres before it's computed — never let that crash the render (Front-End.md §F).
function fmtAcres(v: unknown): string {
  return typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "—";
}

// The drag handle IS the drag source (not the whole row) — a clear affordance that
// doesn't fight the row's checkbox / inline-rename / buttons. Drop targets = folders.
function DragHandle({ fieldId }: { fieldId: string }) {
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_FIELD, fieldId);
        e.dataTransfer.effectAllowed = "move";
        // surfaces the Unfiled drop target for the duration — it is hidden when empty, and dragging a
        // field OUT of a folder is the one time you need it to exist
        useUiStore.getState().setDraggingField(true);
      }}
      onDragEnd={() => useUiStore.getState().setDraggingField(false)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      className="shrink-0 cursor-grab text-faint hover:text-fg active:cursor-grabbing"
      title="Drag onto a folder to file this field"
      aria-label="Drag field into a folder"
    >
      <GripVertical size={14} />
    </span>
  );
}

// One org-wide jobs sweep, shared to every row cell via context (the memoized column defs can't
// close over per-render data without re-creating the columns; a context read keeps them stable).
const OrgJobsCtx = createContext<Record<string, FieldJob[]>>({});
function InProgressCellBound({ fieldId }: { fieldId: string }) {
  const jobsByField = useContext(OrgJobsCtx);
  return <InProgressCell jobs={jobsByField[fieldId]} />;
}

function ActionFlagsCell({ field }: { field: DashboardField }) {
  // Sets saved in this tab live outside React. Dismissals do too. Coverage is
  // the org query; a row renders no nutrient flags until that read succeeds.
  useRecSetsBus((s) => s.version);
  useFlagDismissBus((s) => s.version);
  const coverage = useRxCoverage();
  const serverRows = coverage.status === "success" ? coverage.data : null;
  const year = new Date().getFullYear();
  const dismissed = loadDismissals(field.field_id);
  const flags = computeActionFlags(
    field,
    flagCoverage(field.field_id, serverRows),
    year,
  ).filter((f) => !dismissed.has(dismissKey(f.key, year)));
  if (!flags.length) return <span className="text-[11px] text-faint">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-1 rounded border border-red-400/40 bg-red-400/10 px-1.5 py-0.5 text-[10px] text-red-400"
          title={f.label}
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />{" "}
          {f.label}
          {/* stopPropagation — the row click (select) must not fire from an "x". */}
          <button
            aria-label={`Dismiss “${f.label}”`}
            className="ml-0.5 rounded p-px text-red-400/70 hover:bg-red-400/20 hover:text-red-400"
            onClick={(e) => {
              e.stopPropagation();
              dismissFlag(field.field_id, f.key, year);
            }}
          >
            <X size={9} />
          </button>
        </span>
      ))}
    </div>
  );
}

// The "In Progress" column: a field's active background work + live ETA, from the same per-field
// jobs read the map's work-in-progress pane uses.
function InProgressCell({ jobs }: { jobs: FieldJob[] | undefined }) {
  const summary = jobs ? fieldJobsSummary(jobs, Date.now()) : null;
  if (!summary) return <span className="text-[11px] text-faint">—</span>;
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 text-[11px] text-muted"
      title={jobsSummaryTitle(jobs ?? [], Date.now())}
    >
      <Loader2
        size={11}
        className={`shrink-0 ${summary.anyRunning ? "animate-spin text-accent" : "text-faint"}`}
      />
      <span className="truncate">{summary.label}</span>
      <span className="shrink-0 tabular-nums text-faint">{summary.detail}</span>
      {summary.count > 1 && (
        <span className="shrink-0 rounded bg-raised px-1 text-[9px] text-faint">
          {summary.count}
        </span>
      )}
    </span>
  );
}

function FieldNameCell({ field }: { field: DashboardField }) {
  const renaming = useUiStore((s) => s.renamingFieldId === field.field_id);
  const startRename = useUiStore((s) => s.startRenameField);
  const rename = useRenameField();
  return (
    <div className="flex items-center gap-1.5">
      <DragHandle fieldId={field.field_id} />
      <InlineEdit
        value={field.field_name}
        autoStart={renaming}
        onDone={() => startRename(null)}
        onCommit={(name) => rename.mutate({ fieldId: field.field_id, name })}
        className="font-medium"
      />
    </div>
  );
}

// The "Unfiled" group header — a drop target to move a field/folder back to root.
function UnfiledHeader() {
  const isOver = useUiStore((s) => s.dragOverFolderId === ROOT_DROP);
  const setDragOver = useUiStore((s) => s.setDragOverFolder);
  const moveField = useMoveField();
  const reparent = useReparentFolder();
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(ROOT_DROP);
      }}
      onDragLeave={() => setDragOver(null)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(null);
        const fid = e.dataTransfer.getData(DND_FIELD);
        if (fid) {
          moveField.mutate({ fieldId: fid, folderId: null });
          return;
        }
        const fol = e.dataTransfer.getData(DND_FOLDER);
        if (fol) reparent.mutate({ folderId: fol, parentFolderId: null });
      }}
      className={`px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-faint ${isOver ? "bg-white/10 ring-1 ring-inset ring-white/40" : ""}`}
    >
      Unfiled
    </div>
  );
}

type DisplayItem =
  | {
      kind: "folder";
      folder: Folder;
      depth: number;
      fieldCount: number;
      subfolderCount: number;
      descendantFieldIds: string[];
    }
  | { kind: "unfiled" }
  | { kind: "field"; row: Row<DashboardField>; depth: number };

// Group the (sorted/filtered) rows into the folder tree for rendering. A field-only
// search bypasses grouping and shows a flat list. A folder-name hit keeps the tree.
function buildDisplayItems(
  rows: Row<DashboardField>[],
  folders: Folder[],
  collapsed: Set<string>,
  filtering: boolean,
  opts?: { draggingField?: boolean; visibleFolderIds?: Set<string> | null },
): DisplayItem[] {
  const visibleFolders = opts?.visibleFolderIds ?? null;
  if (filtering && !visibleFolders)
    return rows.map((row) => ({ kind: "field", row, depth: 0 }));

  const folderIds = new Set(folders.map((f) => f.folder_id));
  const childrenOf = new Map<string | null, Folder[]>();
  for (const f of folders) {
    const k =
      f.parent_folder_id && folderIds.has(f.parent_folder_id)
        ? f.parent_folder_id
        : null;
    const arr = childrenOf.get(k) ?? [];
    arr.push(f);
    childrenOf.set(k, arr);
  }
  for (const arr of childrenOf.values())
    arr.sort((a, b) => a.name.localeCompare(b.name));

  const fieldsOf = new Map<string | null, Row<DashboardField>[]>();
  for (const row of rows) {
    const fid = row.original.folder_id;
    const k = fid && folderIds.has(fid) ? fid : null; // stale/deleted folder_id -> Unfiled
    const arr = fieldsOf.get(k) ?? [];
    arr.push(row);
    fieldsOf.set(k, arr);
  }

  // all field ids under a folder (its own + every descendant folder's) — for folder-level select
  const descendantsOf = (folderId: string): string[] => {
    const out = (fieldsOf.get(folderId) ?? []).map((r) => r.original.field_id);
    for (const sub of childrenOf.get(folderId) ?? [])
      out.push(...descendantsOf(sub.folder_id));
    return out;
  };

  const items: DisplayItem[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const folder of childrenOf.get(parentId) ?? []) {
      if (visibleFolders && !visibleFolders.has(folder.folder_id)) continue;
      items.push({
        kind: "folder",
        folder,
        depth,
        // Fields in SUBFOLDERS count towards the parent: a folder holding three subfolders and no
        // loose fields read "0 fields", which is true of its own row and false of what the user put
        // in it.
        fieldCount: descendantsOf(folder.folder_id).length,
        subfolderCount: (childrenOf.get(folder.folder_id) ?? []).length,
        descendantFieldIds: descendantsOf(folder.folder_id),
      });
      if (!collapsed.has(folder.folder_id)) {
        walk(folder.folder_id, depth + 1);
        for (const row of fieldsOf.get(folder.folder_id) ?? [])
          items.push({ kind: "field", row, depth: depth + 1 });
      }
    }
  };
  walk(null, 0);

  const unfiled = fieldsOf.get(null) ?? [];
  if (folders.length > 0) {
    // Only when it holds something -- or while a field is being dragged, since it is also the drop
    // target for moving one back OUT of a folder — it shouldn't sit at the bottom of the scree when
    // empty). Hiding it unconditionally would have quietly removed the only way to unfile a field.
    if (unfiled.length > 0 || opts?.draggingField)
      items.push({ kind: "unfiled" });
    for (const row of unfiled) items.push({ kind: "field", row, depth: 0 });
  } else {
    for (const row of unfiled) items.push({ kind: "field", row, depth: 0 });
  }
  // A field can match by name while its folder does not. The folder stays hidden; the field still shows.
  if (visibleFolders) {
    const shown = new Set(
      items.flatMap((item) => (item.kind === "field" ? [item.row.id] : [])),
    );
    for (const row of rows) {
      if (!shown.has(row.id)) items.push({ kind: "field", row, depth: 0 });
    }
  }
  return items;
}

export function FieldsTable() {
  const { data, isLoading, error } = useFields();
  const coverage = useRxCoverage();
  const serverRows = coverage.status === "success" ? coverage.data : null;
  // ONE org-wide call for every field's active jobs -> the "In Progress" column (context-shared to rows)
  const { data: jobsByField } = useOrgFieldJobs();
  const { data: folders } = useFolders();
  const collapsed = useUiStore((s) => s.collapsedFolders);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Toolbar state lives in the shared store: the search box and the selection actions render in the
  // global header now, so the table can't own them privately any more.
  const globalFilter = useFieldToolsStore((s) => s.query);
  const rowSelection = useFieldToolsStore((s) => s.rowSelection);
  const setRowSelection = useFieldToolsStore((s) => s.setRowSelection);

  // would otherwise linger as orphan keys in the TanStack selection map.
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const clearSelection = useFieldToolsStore((s) => s.clearSelection);
  useEffect(() => {
    clearSelection();
  }, [activeOrgId, clearSelection]);

  const setFieldsSelected = (ids: string[], select: boolean) =>
    setRowSelection((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        if (select) next[id] = true;
        else delete next[id];
      }
      return next;
    });

  const columns = useMemo(
    () => [
      col.display({
        id: "select",
        header: ({ table }) => {
          const visible = table.getRowModel().rows;
          const someVisibleSelected = visible.some((r) => r.getIsSelected());
          return (
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              ref={(el) => {
                if (el)
                  el.indeterminate =
                    someVisibleSelected && !table.getIsAllRowsSelected();
              }}
              onChange={table.getToggleAllRowsSelectedHandler()}
              aria-label="Select all"
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label={`Select ${row.original.field_name}`}
          />
        ),
      }),
      col.accessor("field_name", {
        header: "Field",
        cell: ({ row }) => <FieldNameCell field={row.original} />,
      }),
      col.accessor("acres", {
        header: "Acres",
        cell: (c) => <span className="tnum">{fmtAcres(c.getValue())}</span>,
      }),
      col.display({
        id: "flags",
        header: "Action Flags",
        cell: ({ row }) => <ActionFlagsCell field={row.original} />,
      }),
      col.display({
        id: "inprogress",
        header: "In Progress",
        cell: ({ row }) => (
          <InProgressCellBound fieldId={row.original.field_id} />
        ),
      }),
    ],
    [],
  );

  // Action-Flag filter: keep only fields carrying at least one of the selected flags (e.g. "Needs
  // Updated Soil Map").
  const rxVersion = useRecSetsBus((s) => s.version);
  // dismissed flags leave the filter too — a pill the user x'ed must not keep matching its filter chip
  const dismissVersion = useFlagDismissBus((s) => s.version);
  // The two revisions recompute this memo when a set is saved in this tab or a
  // pill is dismissed. The server rows are the same array the pills read.
  const flagLabelsByField = useMemo(() => {
    const year = new Date().getFullYear();
    const out: Record<string, string[]> = {};
    for (const f of data ?? [])
      out[f.field_id] = activeFlagLabels(
        f,
        year,
        flagCoverage(f.field_id, serverRows),
      );
    void rxVersion;
    void dismissVersion;
    return out;
  }, [data, rxVersion, dismissVersion, serverRows]);

  const folderHit = useMemo(
    () =>
      globalFilter.trim() ? folderSearch(folders ?? [], globalFilter) : null,
    [folders, globalFilter],
  );
  const tableData = useMemo(
    () =>
      (data ?? []).filter((field) =>
        searchKeepsField(
          field,
          flagLabelsByField[field.field_id] ?? [],
          globalFilter,
          folders ?? [],
          folderHit,
        ),
      ),
    [data, flagLabelsByField, globalFilter, folders, folderHit],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    // filter on the TRIMMED query so a whitespace-only value doesn't silently filter rows while the
    // layout flag (`filtering`, also trimmed) keeps the folder-grouped view — they must agree. The
    // input still shows the raw text the user typed.
    state: { sorting, rowSelection },
    getRowId: (row) => row.field_id, // stable selection keyed by field id (folder-select relies on this)
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const rows = table.getRowModel().rows;
  // A folder-name hit keeps the tree and forces those folders open. Any other
  // query still flattens to the matched fields. The stored collapse set is not written.
  const filtering = globalFilter.trim().length > 0;
  const openIds = useMemo(
    () =>
      searchOpenFolderIds(
        folderHit,
        tableData.map((field) => field.folder_id),
        folders ?? [],
      ),
    [folderHit, tableData, folders],
  );
  const treeCollapsed = useMemo(
    () => collapseIgnoringSearch(collapsed, openIds),
    [collapsed, openIds],
  );
  // `draggingField` keeps the Unfiled drop target reachable while a field is in flight, without leaving it
  // parked at the bottom of the screen the rest of the time.
  const draggingField = useUiStore((s) => s.draggingField);
  const items = useMemo(
    () =>
      buildDisplayItems(
        rows,
        folders ?? [],
        treeCollapsed,
        filtering && openIds == null,
        { draggingField, visibleFolderIds: openIds },
      ),
    [rows, folders, treeCollapsed, filtering, openIds, draggingField],
  );
  // Native drag does not scroll for you, so a folder below the fold is unreachable mid-drag. Anchored on
  // the table root: the hook walks up to whatever is actually scrolling (today `<main>` in App.tsx).
  const tableRootRef = useRef<HTMLDivElement>(null);
  useDragAutoScroll(draggingField, tableRootRef);
  const colCount = table.getVisibleLeafColumns().length;

  const selectedIds = useMemo(
    () => new Set(Object.keys(rowSelection).filter((id) => rowSelection[id])),
    [rowSelection],
  );
  const selectedFields = useMemo(
    () => (data ?? []).filter((f) => selectedIds.has(f.field_id)),
    [data, selectedIds],
  );

  if (isLoading)
    return <p className="p-4 text-sm text-muted">Loading fields…</p>;
  if (error)
    return <p className="p-4 text-sm text-red-400">Failed to load fields.</p>;

  // folder checkbox: checked when EVERY descendant field is selected, indeterminate when some are
  const folderSelect = (ids: string[]) => {
    const total = ids.length;
    const sel = ids.filter((id) => selectedIds.has(id)).length;
    return {
      total,
      all: total > 0 && sel === total,
      some: sel > 0 && sel < total,
    };
  };

  return (
    <OrgJobsCtx.Provider value={jobsByField ?? {}}>
      <div className="space-y-3" ref={tableRootRef}>
        {/* Map Soil + Build Rx + the selection count: highlighted, at the top of the page ). The flat header menu keeps only the occasional actions. */}
        <FieldActionsRow selected={selectedFields} />
        <FieldActionPanels selected={selectedFields} />
        <MergeWatchStrip />

        <div className="flex flex-wrap items-center gap-2">
          {/* This note stays: hiding folders is a structural change to what's on screen and silently doing it is disorienting. */}
          {filtering && openIds == null && (
            <span className="text-[11px] text-faint">
              Filtered — folders hidden
            </span>
          )}
        </div>

        {/* Desktop / tablet: table */}
        <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface sm:block">
          <table className="min-w-full text-sm">
            <thead className="bg-raised text-left text-xs text-muted">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} className="px-3 py-2 font-medium">
                      {h.isPlaceholder ? null : (
                        <button
                          className={
                            h.column.getCanSort()
                              ? "inline-flex items-center gap-1"
                              : ""
                          }
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            h.column.columnDef.header,
                            h.getContext(),
                          )}
                          {h.column.getCanSort() && (
                            <ArrowUpDown size={11} className="text-faint" />
                          )}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((it) => {
                if (it.kind === "folder") {
                  const s = folderSelect(it.descendantFieldIds);
                  return (
                    <tr
                      key={`folder-${it.folder.folder_id}`}
                      className="bg-raised/40"
                    >
                      <td colSpan={colCount} className="p-0">
                        <FolderRow
                          folder={it.folder}
                          depth={it.depth}
                          fieldCount={it.fieldCount}
                          subfolderCount={it.subfolderCount}
                          selectAll={s.all}
                          selectSome={s.some}
                          onToggleSelect={() =>
                            setFieldsSelected(it.descendantFieldIds, !s.all)
                          }
                          selectDisabled={s.total === 0}
                          forceExpanded={
                            openIds?.has(it.folder.folder_id) ?? false
                          }
                        />
                      </td>
                    </tr>
                  );
                }
                if (it.kind === "unfiled") {
                  return (
                    <tr key="unfiled">
                      <td colSpan={colCount} className="p-0">
                        <UnfiledHeader />
                      </td>
                    </tr>
                  );
                }
                const row = it.row;
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={
                        row.getIsSelected()
                          ? "bg-emerald-400/10"
                          : "hover:bg-raised/60"
                      }
                    >
                      {row.getVisibleCells().map((cell, ci) => (
                        <td
                          key={cell.id}
                          className="px-3 py-2 align-middle"
                          style={
                            ci === 0
                              ? { paddingLeft: 12 + it.depth * 16 }
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile: card-per-row with folder headers */}
        <div className="space-y-2 sm:hidden">
          {items.map((it) => {
            if (it.kind === "folder") {
              const s = folderSelect(it.descendantFieldIds);
              return (
                <div
                  key={`folder-${it.folder.folder_id}`}
                  className="rounded-md bg-raised/40"
                >
                  <FolderRow
                    folder={it.folder}
                    depth={it.depth}
                    fieldCount={it.fieldCount}
                    subfolderCount={it.subfolderCount}
                    selectAll={s.all}
                    selectSome={s.some}
                    onToggleSelect={() =>
                      setFieldsSelected(it.descendantFieldIds, !s.all)
                    }
                    selectDisabled={s.total === 0}
                    forceExpanded={openIds?.has(it.folder.folder_id) ?? false}
                  />
                </div>
              );
            }
            if (it.kind === "unfiled") return <UnfiledHeader key="unfiled" />;
            const f = it.row.original;
            return (
              <div
                key={it.row.id}
                style={{ marginLeft: it.depth * 12 }}
                className="flex items-center gap-2 rounded-lg border border-line bg-surface p-3"
              >
                <input
                  type="checkbox"
                  checked={it.row.getIsSelected()}
                  onChange={it.row.getToggleSelectedHandler()}
                  aria-label={`Select ${f.field_name}`}
                />
                <div className="min-w-0">
                  <div className="font-medium">
                    <FieldNameCell field={f} />
                  </div>
                  <div className="text-xs text-muted">
                    <span className="tnum">{fmtAcres(f.acres)}</span> ac
                  </div>
                  <div className="mt-1">
                    <ActionFlagsCell field={f} />
                  </div>
                  <div className="mt-1">
                    <InProgressCellBound fieldId={f.field_id} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </OrgJobsCtx.Provider>
  );
}
